import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { EmailService } from '@/services/emailService';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { callFunction } from '@/utils/supabaseFunctions';
import { StandardPageHeader } from '@/components/layout/StandardPageHeader';
import { PageContainer } from '@/components/layout/PageContainer';
import { Mail } from 'lucide-react';

export default function ParentInviteTest() {
  const [email, setEmail] = useState('');
  const [familyId, setFamilyId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{success: boolean; message: string; data?: any} | null>(null);
  const [resendStatus, setResendStatus] = useState<{isConfigured: boolean; apiKeyAvailable: boolean} | null>(null);
  const { user, organizationId } = useAuth();

  const testResendConfig = async () => {
    try {
      setLoading(true);
      setResult(null);
      
      // Use the Edge Function to check Resend configuration on the server side
      const response = await callFunction<any>('resend-config', {
        method: 'GET'
      });
      
      if (response.success) {
        setResendStatus({
          isConfigured: response.resendConfigured,
          apiKeyAvailable: response.resendApiKeyAvailable
        });
      } else {
        throw new Error(response.error || 'Failed to check Resend configuration');
      }
    } catch (error: any) {
      console.error('Error checking Resend configuration:', error);
      setResendStatus({
        isConfigured: false,
        apiKeyAvailable: false
      });
    } finally {
      setLoading(false);
    }
  };

  const testEdgeFunction = async () => {
    try {
      setLoading(true);
      setResult(null);

      // Call the validate-invite edge function with a test token
      const response = await callFunction<any>('validate-invite', {
        method: 'GET',
        queryParams: { token: 'test-token' }
      });

      setResult({
        success: true,
        message: 'Edge function called successfully',
        data: response
      });
    } catch (error: any) {
      setResult({
        success: false,
        message: `Edge function error: ${error.message}`,
        data: error
      });
    } finally {
      setLoading(false);
    }
  };

  const testCreateInvitation = async () => {
    if (!email || !familyId) {
      setResult({
        success: false,
        message: 'Please enter both email and family ID'
      });
      return;
    }

    try {
      setLoading(true);
      setResult(null);

      // Check if we have an organization ID
      if (!organizationId) {
        throw new Error('No organization ID found for current user');
      }

      // Now use EmailService to send an invitation
      const result = await EmailService.sendInvite(email, {
        organization_id: organizationId,
        family_id: familyId,
        firstName: 'Test',
        lastName: 'User',
        organizationName: 'Test Organization',
        created_by: user?.id
      });

      if (result.success) {
        setResult({
          success: true,
          message: 'Invitation created and email sent successfully',
          data: {
            inviteLink: result.inviteLink,
            error: result.error
          }
        });
      } else {
        setResult({
          success: false,
          message: `Failed to create invitation: ${result.error}`,
          data: {
            inviteLink: result.inviteLink
          }
        });
      }
    } catch (error: any) {
      setResult({
        success: false,
        message: `Error creating invitation: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <StandardPageHeader
        title="Parent Invitation Test"
        subtitle="Test parent invitation functionality and email configuration"
        icon={Mail}
      />
      
      <PageContainer maxWidth="full">
        <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Test Resend Configuration</CardTitle>
            <CardDescription>
              Check if Resend email service is properly configured
            </CardDescription>
          </CardHeader>
          <CardContent>
            {resendStatus && (
              <div className="mb-4">
                <Alert variant={resendStatus.isConfigured ? "default" : "destructive"}>
                  <AlertTitle>Resend Configuration Status</AlertTitle>
                  <AlertDescription>
                    <div>API Key Available: <strong>{resendStatus.apiKeyAvailable ? 'Yes' : 'No'}</strong></div>
                    <div>Resend Configured: <strong>{resendStatus.isConfigured ? 'Yes' : 'No'}</strong></div>
                    {!resendStatus.isConfigured && (
                      <div className="mt-2">
                        <p className="font-medium">Resend is not configured properly. You need to set one of the following:</p>
                        <ul className="list-disc pl-5 mt-2 space-y-1">
                          <li>Environment variable <code>RESEND_API_KEY</code> in Supabase</li>
                          <li>Local environment variable <code>VITE_RESEND_API_KEY</code> for development</li>
                        </ul>
                        <p className="mt-2 text-sm">
                          <strong>For Supabase:</strong> Go to Project Settings → Environment Variables → Add a new variable with the key <code>RESEND_API_KEY</code>
                        </p>
                        <p className="mt-1 text-sm">
                          <strong>For local development:</strong> Create a <code>.env.local</code> file with <code>VITE_RESEND_API_KEY=your_api_key</code>
                        </p>
                      </div>
                    )}
                  </AlertDescription>
                </Alert>
              </div>
            )}
          </CardContent>
          <CardFooter>
            <Button onClick={testResendConfig} disabled={loading}>
              {loading ? 'Checking...' : 'Check Resend Configuration'}
            </Button>
          </CardFooter>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Test Edge Function</CardTitle>
            <CardDescription>
              Test if the validate-invite Edge Function is accessible
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p>
                Click the button below to test the Edge Function. This will help determine if:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>The Edge Function is deployed properly</li>
                <li>Your authentication is working correctly</li>
                <li>CORS is properly configured</li>
              </ul>
              
              <div className="bg-blue-50 rounded-lg p-3 text-sm">
                <p className="font-medium">If the test fails:</p>
                <ol className="list-decimal pl-5 mt-1 space-y-1">
                  <li>Check that the function is deployed using <code>supabase functions list</code></li>
                  <li>Deploy the function using <code>node scripts/deploy-edge-functions.js</code></li>
                  <li>Check the browser console for detailed error messages</li>
                </ol>
              </div>
              
              {result && (
                <Alert variant={result.success ? "default" : "destructive"} className="mt-3">
                  <AlertTitle>{result.success ? 'Success' : 'Error'}</AlertTitle>
                  <AlertDescription>
                    <p>{result.message}</p>
                    {result.data && (
                      <div className="mt-2 text-xs">
                        <pre className="whitespace-pre-wrap bg-muted p-2 rounded">
                          {JSON.stringify(result.data, null, 2)}
                        </pre>
                      </div>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </CardContent>
          <CardFooter>
            <Button onClick={testEdgeFunction} disabled={loading}>
              {loading ? 'Testing...' : 'Test Edge Function'}
            </Button>
          </CardFooter>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Create Parent Invitation</CardTitle>
            <CardDescription>
              Create a real parent invitation to test the complete flow
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="parent@example.com"
                />
              </div>

              <div>
                <Label htmlFor="family-id">Family ID</Label>
                <Input
                  id="family-id"
                  value={familyId}
                  onChange={(e) => setFamilyId(e.target.value)}
                  placeholder="UUID of family"
                />
              </div>
              
              {result && (
                <Alert variant={result.success ? "default" : "destructive"}>
                  <AlertTitle>{result.success ? 'Success' : 'Error'}</AlertTitle>
                  <AlertDescription>
                    <p>{result.message}</p>
                    {result.data && (
                      <div className="mt-2 text-xs">
                        <pre className="whitespace-pre-wrap bg-muted p-2 rounded">
                          {JSON.stringify(result.data, null, 2)}
                        </pre>
                      </div>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </CardContent>
          <CardFooter>
            <Button onClick={testCreateInvitation} disabled={loading}>
              {loading ? 'Creating...' : 'Create Invitation'}
            </Button>
          </CardFooter>
        </Card>
        </div>
      </PageContainer>
    </div>
  );
} 